import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightCircle, X } from 'lucide-react';
import { EnhancedDeal, ProjectPLBucket, PROJECT_PL_BUCKETS } from '../../types';
import {
  PROJECT_PL_BUCKET_LABELS,
  ProjectPLExpenseRow,
  ProjectPLPaymentRow,
  ProjectPLRow as ProjectPLRowData,
} from '../../utils/projectPL';
import { suggestedDealIdsForQbTransaction } from '../../utils/qbDealSuggest';
import { useCRM } from '../../utils/CRMContext';
import { buildQBLinkDealOptions, encodeQBLinkTargetId } from '../../utils/qbDealLinkOptions';
import { QB_EXPENSE_OVERHEAD_ASSIGN } from '../../utils/qbTransactionLinks';
import { groupActivityByProposalNode, type ActivityFeedItem } from '../../utils/projectPLActivityGroups';
import QBDealPicker, { QBDealOption } from './QBDealPicker';

interface ProjectPLActivityFeedProps {
  row: ProjectPLRowData;
  formatCurrencyFull: (n: number) => string;
  readOnly?: boolean;
  embedded?: boolean;
  hideHeader?: boolean;
  onClose?: () => void;
  headerRight?: React.ReactNode;
  onExpenseBucketChange?: (expenseId: string, bucket: ProjectPLBucket | null) => Promise<void>;
  onReassignPayment?: (paymentId: string, dealId: string | null) => Promise<void>;
  onReassignExpense?: (expenseId: string, target: string) => Promise<void>;
  reassignableDeals?: EnhancedDeal[];
}

type FeedItem = ActivityFeedItem;

function parseFeedDate(value: string | Date | null | undefined): number {
  if (!value) return 0;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : 0;
  }
  const raw = value.length === 10 ? `${value}T12:00:00` : value;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : 0;
}

function formatFeedDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  if (value instanceof Date) return value.toLocaleDateString();
  const ms = parseFeedDate(value);
  return ms ? new Date(ms).toLocaleDateString() : value;
}

function buildFeedItems(row: ProjectPLRowData): FeedItem[] {
  const items: FeedItem[] = [
    ...row.payments.map(payment => ({
      kind: 'in' as const,
      id: `in-${payment.id}`,
      sortMs: parseFeedDate(payment.paidAt),
      payment,
    })),
    ...row.expenses.map(expense => ({
      kind: 'out' as const,
      id: `out-${expense.id}`,
      sortMs: parseFeedDate(expense.txnDate),
      expense,
    })),
  ];
  items.sort((a, b) => b.sortMs - a.sortMs || a.id.localeCompare(b.id));
  return items;
}

const ProjectPLActivityFeed: React.FC<ProjectPLActivityFeedProps> = ({
  row,
  formatCurrencyFull,
  readOnly,
  embedded,
  hideHeader,
  onClose,
  headerRight,
  onExpenseBucketChange,
  onReassignPayment,
  onReassignExpense,
  reassignableDeals,
}) => {
  const { timberProjects } = useCRM();
  const [savingExpenseId, setSavingExpenseId] = useState<string | null>(null);
  const [savingExpenseProjectId, setSavingExpenseProjectId] = useState<string | null>(null);
  const [reassigningPaymentId, setReassigningPaymentId] = useState<string | null>(null);
  const [savingPaymentId, setSavingPaymentId] = useState<string | null>(null);

  const items = useMemo(() => buildFeedItems(row), [row]);
  const groups = useMemo(() => groupActivityByProposalNode(items), [items]);

  const dealPickerOptions = useMemo(
    () =>
      buildQBLinkDealOptions(reassignableDeals || [], timberProjects).map(o => ({
        id: o.id,
        label: o.label,
        searchText: o.searchText,
      })),
    [reassignableDeals, timberProjects],
  );

  const expenseSpecialOptions = useMemo<QBDealOption[]>(
    () => [{
      id: QB_EXPENSE_OVERHEAD_ASSIGN,
      label: 'Company overhead (shared pool)',
      searchText: 'overhead shared company pool',
    }],
    [],
  );

  const handleBucketChange = useCallback(
    async (expenseId: string, value: string) => {
      if (!onExpenseBucketChange) return;
      setSavingExpenseId(expenseId);
      try {
        await onExpenseBucketChange(expenseId, (value || null) as ProjectPLBucket | null);
      } finally {
        setSavingExpenseId(null);
      }
    },
    [onExpenseBucketChange],
  );

  const handleReassignPayment = useCallback(
    async (paymentId: string, dealId: string) => {
      if (!onReassignPayment) return;
      setSavingPaymentId(paymentId);
      try {
        await onReassignPayment(paymentId, dealId || null);
        setReassigningPaymentId(null);
      } finally {
        setSavingPaymentId(null);
      }
    },
    [onReassignPayment],
  );

  const handleReassignExpense = useCallback(
    async (expenseId: string, target: string) => {
      if (!onReassignExpense) return;
      setSavingExpenseProjectId(expenseId);
      try {
        await onReassignExpense(expenseId, target);
      } finally {
        setSavingExpenseProjectId(null);
      }
    },
    [onReassignExpense],
  );

  const expenseProjectValue = useCallback(
    (e: ProjectPLExpenseRow) => {
      if (e.category === 'overhead') return QB_EXPENSE_OVERHEAD_ASSIGN;
      const dealId = e.matchedDealId || row.dealId;
      if (e.projectPhase && dealId) {
        return encodeQBLinkTargetId(dealId, e.projectPhase);
      }
      return dealId;
    },
    [row.dealId],
  );

  return (
    <div className={embedded ? 'sa-ppl-feed-embed' : 'sa-ppl-section sa-ppl-section--feed'}>
      {!hideHeader && (
      <div className="sa-ppl-section-head">
        <span>Activity</span>
        <span className="sa-ppl-section-hint">
          {items.length}
          {row.cashCollected > 0 ? ` · ${formatCurrencyFull(row.cashCollected)} in` : ''}
        </span>
        {headerRight}
        {onClose && (
          <button type="button" className="sa-ppl-feed-close" onClick={onClose} aria-label="Close activity">
            <X size={14} />
          </button>
        )}
      </div>
      )}
      {items.length === 0 ? (
        <div className="sa-ppl-empty">No payments or expenses yet.</div>
      ) : (
        <div className="sa-ppl-feed">
          {groups.map(group => (
            <section key={group.id} className="sa-ppl-feed-group">
              <div className="sa-ppl-feed-group-head">
                <span className="sa-ppl-feed-group-label">
                  {group.id === 'collected' ? 'Collected' : PROJECT_PL_BUCKET_LABELS[group.id]}
                  <span className="sa-ppl-feed-group-count">{group.items.length}</span>
                </span>
                <span className={`sa-ppl-feed-amt ${group.total >= 0 ? 'sa-ppl-feed-amt--in' : 'sa-ppl-feed-amt--out'}`}>
                  {group.total >= 0 ? '+' : '−'}
                  {formatCurrencyFull(Math.abs(group.total))}
                </span>
              </div>
              {group.items.map(item => {
            if (item.kind === 'in') {
              const p = item.payment;
              const isQbPayment = p.source === 'quickbooks';
              const canReassign =
                isQbPayment && !readOnly && Boolean(onReassignPayment) && dealPickerOptions.length > 0;
              const isEditing = reassigningPaymentId === p.id;
              return (
                <div
                  key={item.id}
                  className={`sa-ppl-feed-row sa-ppl-feed-row--in${p.excluded ? ' is-excluded' : ''}`}
                >
                  <div className="sa-ppl-feed-main">
                    <div className="sa-ppl-feed-who">
                      <div className="sa-ppl-feed-title">{p.description || 'Payment'}</div>
                      <div className="sa-ppl-feed-sub">
                        {p.source === 'design-fee' ? 'Design fee' : 'QuickBooks'}
                        {' · '}
                        {formatFeedDate(p.paidAt)}
                      </div>
                    </div>
                    <div className="sa-ppl-feed-amt sa-ppl-feed-amt--in">
                      +{formatCurrencyFull(p.amount)}
                    </div>
                  </div>
                  {canReassign && (
                    <div className="sa-ppl-feed-actions">
                      {isEditing ? (
                        <div className="sa-ppl-payment-reassign">
                          <select
                            className="sa-ppl-bucket-select"
                            defaultValue={row.dealId}
                            disabled={savingPaymentId === p.id}
                            onChange={ev => handleReassignPayment(p.id, ev.target.value)}
                            aria-label="Reassign to deal"
                          >
                            <option value="">— Detach (unmatched) —</option>
                            {dealPickerOptions.map(d => (
                              <option key={d.id} value={d.id}>
                                {d.label}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="sa-ppl-bucket-clear"
                            disabled={savingPaymentId === p.id}
                            onClick={() => setReassigningPaymentId(null)}
                            title="Cancel"
                          >
                            <X size={11} />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="sa-ppl-reassign-btn"
                          onClick={() => setReassigningPaymentId(p.id)}
                          title="Move this payment to a different project."
                        >
                          <ArrowRightCircle size={11} />
                          Reassign
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            }

            const e = item.expense;
            const canReassignExpense =
              !readOnly && Boolean(onReassignExpense) && dealPickerOptions.length > 0;
            const suggestedIds = suggestedDealIdsForQbTransaction(reassignableDeals || [], {
              vendorName: e.vendorName,
              description: e.description,
              customerName: e.customerName,
            });
            const canEditBucket = Boolean(onExpenseBucketChange) && !readOnly;
            return (
              <div key={item.id} className="sa-ppl-feed-row sa-ppl-feed-row--out">
                <div className="sa-ppl-feed-main">
                  <div className="sa-ppl-feed-who">
                    <div className="sa-ppl-feed-title">{e.vendorName}</div>
                    {(e.description || e.accountName) && (
                      <div className="sa-ppl-feed-sub" title={e.description || e.accountName}>
                        {[e.description, e.accountName].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                  <div className="sa-ppl-feed-amt sa-ppl-feed-amt--out">
                    −{formatCurrencyFull(e.amount)}
                  </div>
                </div>
                {(canReassignExpense || canEditBucket) && (
                  <div className="sa-ppl-feed-actions">
                    <span className="sa-ppl-feed-date">{formatFeedDate(e.txnDate)}</span>
                    <span className={`sa-ppl-po-chip${e.poNumber ? '' : ' sa-ppl-po-chip--none'}`}>
                      {e.poNumber || 'No PO'}
                    </span>
                    {canReassignExpense && (
                      <QBDealPicker
                        value={expenseProjectValue(e)}
                        dealOptions={dealPickerOptions}
                        specialOptions={expenseSpecialOptions}
                        suggestedIds={suggestedIds}
                        emptyLabel="— Unmatched —"
                        disabled={savingExpenseProjectId === e.id}
                        onChange={target => handleReassignExpense(e.id, target)}
                        aria-label="Expense project assignment"
                      />
                    )}
                    {canEditBucket && (
                      <>
                        <select
                          className={`sa-ppl-bucket-select ${e.hasOverride ? 'has-override' : ''}`}
                          value={e.bucket}
                          onChange={ev => handleBucketChange(e.id, ev.target.value)}
                          disabled={savingExpenseId === e.id}
                        >
                          {PROJECT_PL_BUCKETS.map(b => (
                            <option key={b} value={b}>
                              {PROJECT_PL_BUCKET_LABELS[b]}
                            </option>
                          ))}
                        </select>
                        {e.hasOverride && (
                          <button
                            type="button"
                            className="sa-ppl-bucket-clear"
                            disabled={savingExpenseId === e.id}
                            onClick={() => handleBucketChange(e.id, '')}
                            title={`Reset to auto (${PROJECT_PL_BUCKET_LABELS[e.autoBucket]})`}
                          >
                            Reset
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
                {!canReassignExpense && !canEditBucket && (
                  <div className="sa-ppl-feed-actions">
                    <span className="sa-ppl-feed-date">{formatFeedDate(e.txnDate)}</span>
                    <span className={`sa-ppl-po-chip${e.poNumber ? '' : ' sa-ppl-po-chip--none'}`}>
                      {e.poNumber || 'No PO'}
                    </span>
                    <span className="sa-ppl-feed-bucket">{PROJECT_PL_BUCKET_LABELS[e.bucket]}</span>
                  </div>
                )}
              </div>
            );
              })}
            </section>
          ))}
        </div>
      )}
      {(row.pos || []).length > 0 && (
        <div className="sa-ppl-po-block">
          <div className="sa-ppl-po-block-head">Purchase orders</div>
          <div className="sa-ppl-po-list">
            {row.pos.map(item => (
              <Link
                key={item.id}
                className="sa-ppl-po-row"
                to={`/purchasing?pr=${encodeURIComponent(item.id)}`}
              >
                <div className="sa-ppl-po-main">
                  <span className="sa-ppl-po-num">{item.label}</span>
                  <span className={`sa-ppl-po-qb sa-ppl-po-qb--${item.qbStatus}`}>{item.qbLabel}</span>
                  <span className="sa-ppl-po-amt">{formatCurrencyFull(item.amount)}</span>
                </div>
                <div className="sa-ppl-po-sub">
                  {[item.vendorName, item.title, item.bucketLabel, item.txnDate && formatFeedDate(item.txnDate)]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectPLActivityFeed;
